import { Controller } from "@hotwired/stimulus"
import { getCsrfToken } from "../../utils/csrf.util"
import { isAbortError } from "../../utils/fetch-error.util"
import { getUserLocale, parseLocaleNumber } from "../../utils/number-format.util"

// Only what Documents::SettlementInvoicesController#diff_preview reads (its
// DIFF_PREVIEW_ITEM_KEYS). The rest of the form — notes, customer, its
// authenticity token — would only grow every request.
const DIFF_PREVIEW_PARAM =
  /^(?:proforma_ids\[\]|settlement_invoice\[document_items_attributes\]\[[^\]]+\]\[(?:id|name|quantity|unit_price|tax_rate|_destroy)\])$/

// What changes the draft: typing, and removing an item (documents--items-manager
// only flips the row's hidden `_destroy`, which fires no input event).
const FORM_CHANGE_EVENTS = ["input", "documents--items-manager:itemRemoved"]

// Connects to data-controller="documents--diff-preview"
//
// Drives the side-by-side "Source proformas vs. Settlement invoice" diff
// panel on the settlement-invoice new form. Whenever the user edits the
// form (input event) or toggles a source proforma (custom
// "documents:proformasChanged" event), this controller fetches the rendered
// diff partial from the backend and swaps it into the preview frame.
//
// Request lifecycle:
//   - debounced via setTimeout (debounceMsValue, default 250ms)
//   - in-flight request cancelled with AbortController
//   - on fetch error or a non-2xx response (not abort) we leave the prior
//     preview untouched and re-raise to the global error handler
export default class extends Controller {
  static targets = ["previewFrame", "form"]
  static values = {
    previewUrl: String,
    debounceMs: { type: Number, default: 250 },
    formId: String,
    // The settlement's source proformas. The form itself carries only the
    // anchor's `proforma_id`, which the endpoint does not read.
    proformaIds: { type: Array, default: [] }
  }

  connect() {
    this.debounceTimer = null
    this.abortController = null
    this.boundRefresh = () => this.refresh()
    this.attachedForm = this.#resolveForm()
    if (this.attachedForm) {
      for (const type of FORM_CHANGE_EVENTS) this.attachedForm.addEventListener(type, this.boundRefresh)
    }
    this.refresh()
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
    if (this.attachedForm && this.boundRefresh) {
      for (const type of FORM_CHANGE_EVENTS) this.attachedForm.removeEventListener(type, this.boundRefresh)
      this.attachedForm = null
    }
  }

  refresh() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null
      this.#performFetch()
    }, this.debounceMsValue)
  }

  async #performFetch() {
    if (!this.hasPreviewFrameTarget || !this.previewUrlValue) return

    // Abort any in-flight request before issuing a new one.
    if (this.abortController) {
      this.abortController.abort()
    }
    this.abortController = new AbortController()

    try {
      // POST, not GET: the draft grows with every item, and as a query string
      // it outgrew Puma's 10 KB limit on a long settlement.
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: { Accept: "text/html", "X-CSRF-Token": getCsrfToken() },
        body: this.#buildParams(),
        signal: this.abortController.signal,
        credentials: "same-origin"
      })

      if (!response.ok) throw new Error(`diff-preview: HTTP ${response.status}`)

      const html = await response.text()
      this.previewFrameTarget.innerHTML = html
    } catch (error) {
      if (isAbortError(error)) return
      console.error("diff-preview: fetch failed", error)
      // console.error alone reaches nothing — browser console collection is off
      // in Better Stack, so re-raise to hit the global handler.
      throw error
    }
  }

  #buildParams() {
    const params = new URLSearchParams()
    const form = this.#resolveForm()
    const proformaIds = new Set(this.proformaIdsValue.map(String))

    if (form) {
      const formatted = this.#formattedNumberNames(form)
      const locale = getUserLocale()
      const formData = new FormData(form)
      for (const [key, value] of formData.entries()) {
        // FormData yields File objects for file inputs — skip them.
        if (typeof value !== "string" || !DIFF_PREVIEW_PARAM.test(key)) continue
        if (key === "proforma_ids[]") {
          proformaIds.add(value)
          continue
        }
        // "1 000,00" is what the user sees; the endpoint reads a plain number,
        // as form--number-parser sends it on submit.
        const sent = formatted.has(key) && value ? (parseLocaleNumber(value, locale) ?? "") : value
        params.append(key, sent)
      }
    }

    for (const id of proformaIds) params.append("proforma_ids[]", id)

    return params
  }

  // Names of the inputs the number formatter shows in the user's locale — the
  // same selection form--number-parser converts back on submit.
  #formattedNumberNames(form) {
    return new Set(
      Array.from(form.querySelectorAll('input[type="text"], input[type="number"], textarea'))
        .filter((input) => input.getAttribute("data-action")?.includes("formatNumber"))
        .map((input) => input.name)
    )
  }

  #resolveForm() {
    if (this.hasFormTarget) return this.formTarget
    if (this.formIdValue) {
      const byId = document.getElementById(this.formIdValue)
      if (byId) return byId
    }
    return this.element.querySelector("form")
  }
}
